import { backendApiRequestGate, isBackendApiRequest } from "./requestGate.mjs";

export type RequestFailureKind = "timeout" | "aborted" | "network" | "unknown";

export class HttpError extends Error {
  readonly status: number;
  readonly responseBody: string | null;

  constructor(status: number, message: string, responseBody: string | null = null) {
    super(message);
    this.name = "HttpError";
    this.status = status;
    this.responseBody = responseBody;
  }

  get isConflict(): boolean { return this.status === 409; }
  get isValidation(): boolean { return this.status === 400 || this.status === 413 || this.status === 422; }
  get isAuth(): boolean { return this.status === 401 || this.status === 403; }
  get isRetryable(): boolean { return this.status === 408 || this.status === 429 || this.status >= 500; }
}


/**
 * Eigener Timeout-Typ, der absichtlich weiterhin ein AbortError ist. Bestehende
 * Aufrufer, die AbortError als vorübergehenden Transportfehler behandeln,
 * bleiben dadurch kompatibel; neue Aufrufer können Timeout trotzdem eindeutig
 * von einem echten Netzwerkfehler unterscheiden.
 */
export class RequestTimeoutError extends DOMException {
  readonly timeoutMs: number;
  readonly requestFailureKind = "timeout" as const;

  constructor(timeoutMs: number, cause?: unknown) {
    super(`Zeitüberschreitung nach ${timeoutMs} ms.`, "AbortError");
    this.timeoutMs = timeoutMs;
    if (cause !== undefined) {
      try { Object.defineProperty(this, "cause", { value: cause, configurable: true }); } catch { /* optional */ }
    }
  }
}

export function classifyRequestFailure(error: unknown): RequestFailureKind {
  let current: unknown = error;
  const visited = new Set<unknown>();
  for (let depth = 0; depth < 5 && current != null && !visited.has(current); depth += 1) {
    visited.add(current);
    if (current instanceof RequestTimeoutError) return "timeout";
    if (current instanceof DOMException && current.name === "AbortError") return "aborted";
    if (current instanceof TypeError) return "network";

    const message = current instanceof Error ? current.message.toLowerCase() : String(current ?? "").toLowerCase();
    if (/zeitüberschreitung|timed?\s*out|timeout/.test(message)) return "timeout";
    if (/failed to fetch|load failed|networkerror|network request failed|netzwerk/.test(message)) return "network";
    if (/aborterror|abgebrochen|aborted/.test(message)) return "aborted";
    current = current instanceof Error && "cause" in current ? current.cause : null;
  }
  return "unknown";
}

export function isRequestTimeout(error: unknown): boolean {
  return classifyRequestFailure(error) === "timeout";
}

export function isRequestNetworkFailure(error: unknown): boolean {
  return classifyRequestFailure(error) === "network";
}

export async function fetchWithTimeout(
  input: RequestInfo | URL,
  init: RequestInit = {},
  timeoutMs = 2500,
): Promise<Response> {
  const externalSignal = init.signal;
  const releaseRequestSlot = isBackendApiRequest(input)
    ? await backendApiRequestGate.acquire(externalSignal)
    : null;
  const controller = new AbortController();
  let timedOut = false;

  const abortFromExternal = () => controller.abort(externalSignal?.reason);
  if (externalSignal) {
    if (externalSignal.aborted) controller.abort(externalSignal.reason);
    else externalSignal.addEventListener("abort", abortFromExternal, { once: true });
  }

  const timer = window.setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, timeoutMs);

  try {
    return await fetch(input, { ...init, signal: controller.signal });
  } catch (error) {
    if (timedOut) throw new RequestTimeoutError(timeoutMs, error);
    throw error;
  } finally {
    window.clearTimeout(timer);
    externalSignal?.removeEventListener("abort", abortFromExternal);
    releaseRequestSlot?.();
  }
}
